import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Upload, Cloud, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EmailForwardingTab } from "@/components/data/EmailForwardingTab";
import { EmailIntegrationTab } from "@/components/data/EmailIntegrationTab";
import { ManualUploadTab } from "@/components/data/ManualUploadTab";

export default function DocumentSources() {
  const { orgId, familyId } = useTenant();
  const [activeTab, setActiveTab] = useState("email-forwarding");

  // Fetch document statistics
  const { data: docStats } = useQuery({
    queryKey: ["document-stats", orgId, familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("type, processing_status, created_at")
        .eq("org_id", orgId!)
        .match(familyId ? { family_id: familyId } : {});

      if (error) throw error;

      const holdingsCount = data.filter(d => d.type === "holdings").length;
      const transactionsCount = data.filter(d => d.type === "transactions").length;
      const statementsCount = data.filter(d => d.type === "statement").length;
      const pendingCount = data.filter(d => d.processing_status === "pending").length;

      return { holdingsCount, transactionsCount, statementsCount, pendingCount, total: data.length };
    },
    enabled: !!orgId,
  });

  // Fetch recent activity
  const { data: recentDocs } = useQuery({
    queryKey: ["recent-documents", orgId, familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("id, type, source, created_at, processing_status")
        .eq("org_id", orgId!)
        .match(familyId ? { family_id: familyId } : {})
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
      case "extracted":
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "pending":
        return <Clock className="h-4 w-4 text-warning" />;
      default:
        return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      onboarding_upload: "Onboarding",
      email_forwarding: "Email Forwarding",
      gmail_sync: "Gmail",
      outlook_sync: "Outlook",
      manual_upload: "Manual Upload",
    };
    return labels[source] || source;
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Document Sources</h1>
        <p className="text-muted-foreground mt-1">
          Manage how documents are ingested into Vintage
        </p>
      </div>

      {/* Source of Truth Status */}
      <Card>
        <CardHeader>
          <CardTitle>Source of Truth Status</CardTitle>
          <CardDescription>Overview of your document coverage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Total Documents</p>
                <p className="text-2xl font-bold">{docStats?.total || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-success" />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Holdings</p>
                <p className="text-2xl font-bold">{docStats?.holdingsCount || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-success" />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-2xl font-bold">{docStats?.transactionsCount || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-success" />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Pending Processing</p>
                <p className="text-2xl font-bold">{docStats?.pendingCount || 0}</p>
              </div>
              {docStats?.pendingCount && docStats.pendingCount > 0 ? (
                <Clock className="h-8 w-8 text-warning" />
              ) : (
                <CheckCircle2 className="h-8 w-8 text-success" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ingestion Methods */}
      <Card>
        <CardHeader>
          <CardTitle>Active Ingestion Methods</CardTitle>
          <CardDescription>Configure how documents are received and processed</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="email-forwarding">
                <Mail className="h-4 w-4" />
                Email Forwarding
              </TabsTrigger>
              <TabsTrigger value="gmail-outlook">
                <Mail className="h-4 w-4" />
                Gmail/Outlook
              </TabsTrigger>
              <TabsTrigger value="manual-upload">
                <Upload className="h-4 w-4" />
                Manual Upload
              </TabsTrigger>
              <TabsTrigger value="cloud-storage" disabled>
                <Cloud className="h-4 w-4" />
                Cloud Storage
                <Badge variant="outline" className="ml-2">Soon</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email-forwarding" className="mt-6">
              <EmailForwardingTab />
            </TabsContent>

            <TabsContent value="gmail-outlook" className="mt-6">
              <EmailIntegrationTab />
            </TabsContent>

            <TabsContent value="manual-upload" className="mt-6">
              <ManualUploadTab />
            </TabsContent>

            <TabsContent value="cloud-storage" className="mt-6">
              <div className="text-center py-8">
                <Cloud className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Cloud Storage Coming Soon</h3>
                <p className="text-sm text-muted-foreground">
                  Direct integrations with Dropbox, Google Drive, and Box will be available soon.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest document ingestion events</CardDescription>
        </CardHeader>
        <CardContent>
          {recentDocs && recentDocs.length > 0 ? (
            <div className="space-y-3">
              {recentDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(doc.processing_status || "pending")}
                    <div>
                      <p className="font-medium">{doc.type || "Document"}</p>
                      <p className="text-sm text-muted-foreground">
                        via {getSourceLabel(doc.source || "")} • {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge variant={doc.processing_status === "extracted" ? "default" : "secondary"}>
                    {doc.processing_status || "pending"}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No recent activity</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
